import React from 'react';
import { format, startOfDay, addMinutes, isSameDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Appointment, CalendarWorkingSettings } from './types';
import { CALENDAR_SETTINGS } from '../../config/calendarSettings';
import {
  APPOINTMENT_CARD_BASE_CLASS,
  APPOINTMENT_CARD_PADDING_COMPACT_CLASS,
  APPOINTMENT_CARD_PADDING_REGULAR_CLASS,
  APPOINTMENT_CARD_TITLE_CLASS,
  APPOINTMENT_CARD_TIME_CLASS,
  APPOINTMENT_CARD_META_CLASS,
  getAppointmentStatusCardClass,
} from './statusStyles';
import {
  BASE_HOUR_SLOT_HEIGHT_PX,
  DEFAULT_WORKDAY_START_TIME,
  DEFAULT_WORKDAY_END_TIME,
  DEFAULT_BREAK_START_TIME,
  DEFAULT_BREAK_END_TIME,
} from '../../config/workingTimeDefaults';

interface DayViewProps {
  currentDate: Date;
  appointments: Appointment[];
  workingSettings?: CalendarWorkingSettings;
  onAppointmentClick: (appointment: Appointment) => void;
  onTimeSlotClick: (startTime: Date, duration: number) => void;
  isLoading?: boolean;
}

interface PositionedAppointment {
  appointment: Appointment;
  startTime: Date;
  endTime: Date;
  top: number;
  height: number;
  column: number;
  columns: number;
}

interface NormalizedAppointment {
  appointment: Appointment;
  startTime: Date;
  endTime: Date;
}

interface ColumnAssignment extends NormalizedAppointment {
  column: number;
}

const APPOINTMENT_VERTICAL_MARGIN_PX = 4;

const normalizeAppointments = (
  dayAppointments: Appointment[],
  dayStart: Date,
  dayEnd: Date
): NormalizedAppointment[] => {
  const normalizedAppointments: NormalizedAppointment[] = [];

  for (const appointment of dayAppointments) {
    const rawStart = new Date(appointment.startTime);
    const rawEnd = new Date(appointment.endTime);
    const startTime = new Date(Math.max(rawStart.getTime(), dayStart.getTime()));
    const endTime = new Date(Math.min(rawEnd.getTime(), dayEnd.getTime()));

    if (endTime <= startTime) {
      continue;
    }

    normalizedAppointments.push({ appointment, startTime, endTime });
  }

  normalizedAppointments.sort((a, b) => {
    const startDiff = a.startTime.getTime() - b.startTime.getTime();
    if (startDiff !== 0) {
      return startDiff;
    }

    return a.endTime.getTime() - b.endTime.getTime();
  });

  return normalizedAppointments;
};

const groupOverlappingAppointments = (
  normalizedAppointments: NormalizedAppointment[]
): NormalizedAppointment[][] => {
  if (normalizedAppointments.length === 0) {
    return [];
  }

  const groups: NormalizedAppointment[][] = [];
  let currentGroup: NormalizedAppointment[] = [];
  let currentGroupEnd = 0;

  for (const appointment of normalizedAppointments) {
    const appointmentStart = appointment.startTime.getTime();
    const appointmentEnd = appointment.endTime.getTime();

    if (currentGroup.length === 0 || appointmentStart < currentGroupEnd) {
      currentGroup.push(appointment);
      currentGroupEnd = Math.max(currentGroupEnd, appointmentEnd);
      continue;
    }

    groups.push(currentGroup);
    currentGroup = [appointment];
    currentGroupEnd = appointmentEnd;
  }

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
};

const assignColumns = (group: NormalizedAppointment[]): { assignments: ColumnAssignment[]; columns: number } => {
  const columnsEndTimes: number[] = [];
  const assignments: ColumnAssignment[] = [];

  for (const item of group) {
    let column = -1;

    for (let index = 0; index < columnsEndTimes.length; index += 1) {
      if (item.startTime.getTime() >= columnsEndTimes[index]) {
        column = index;
        break;
      }
    }

    if (column < 0) {
      column = columnsEndTimes.length;
      columnsEndTimes.push(0);
    }

    columnsEndTimes[column] = item.endTime.getTime();
    assignments.push({ ...item, column });
  }

  return {
    assignments,
    columns: Math.max(1, columnsEndTimes.length),
  };
};

const toPositionedAppointment = (
  assignment: ColumnAssignment,
  columns: number,
  dayStart: Date,
  minutePixelRatio: number
): PositionedAppointment => {
  const minutesFromStart = Math.max(
    0,
    Math.round((assignment.startTime.getTime() - dayStart.getTime()) / 60000)
  );
  const appointmentMinutes = Math.max(
    1,
    Math.round((assignment.endTime.getTime() - assignment.startTime.getTime()) / 60000)
  );

  return {
    appointment: assignment.appointment,
    startTime: assignment.startTime,
    endTime: assignment.endTime,
    top: Math.round(minutesFromStart * minutePixelRatio) + APPOINTMENT_VERTICAL_MARGIN_PX,
    height: Math.max(
      14,
      Math.round(appointmentMinutes * minutePixelRatio) - APPOINTMENT_VERTICAL_MARGIN_PX * 2
    ),
    column: assignment.column,
    columns,
  };
};

const buildPositionedAppointments = (
  dayAppointments: Appointment[],
  dayStart: Date,
  dayEnd: Date,
  minutePixelRatio: number
): PositionedAppointment[] => {
  const normalizedAppointments = normalizeAppointments(dayAppointments, dayStart, dayEnd);
  const groups = groupOverlappingAppointments(normalizedAppointments);
  const positioned: PositionedAppointment[] = [];

  for (const group of groups) {
    const { assignments, columns } = assignColumns(group);

    for (const assignment of assignments) {
      positioned.push(toPositionedAppointment(assignment, columns, dayStart, minutePixelRatio));
    }
  }

  return positioned;
};

const parseTimeToMinutes = (value: string): number | null => {
  const parts = value.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  return hours * 60 + minutes;
};

const hasMinutesOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number
): boolean => {
  return startA < endB && endA > startB;
};

const DayView: React.FC<DayViewProps> = ({
  currentDate,
  appointments,
  workingSettings,
  onAppointmentClick,
  onTimeSlotClick,
  isLoading = false
}) => {
  // Configuration des heures de travail
  const bookingStepMinutes = CALENDAR_SETTINGS.bookingStepMinutes;
  const slotDuration = bookingStepMinutes;
  const slotHeight = Math.max(
    28,
    Math.round((slotDuration / 60) * BASE_HOUR_SLOT_HEIGHT_PX)
  );

  const workdayStartMinutes =
    parseTimeToMinutes(workingSettings?.workdayStartTime || DEFAULT_WORKDAY_START_TIME) ??
    CALENDAR_SETTINGS.workStartHour * 60;
  const workdayEndMinutes =
    parseTimeToMinutes(workingSettings?.workdayEndTime || DEFAULT_WORKDAY_END_TIME) ??
    CALENDAR_SETTINGS.workEndHour * 60;

  const breakStartMinutes = workingSettings?.enableMiddayBreak
    ? parseTimeToMinutes(workingSettings.breakStartTime || DEFAULT_BREAK_START_TIME)
    : null;
  const breakEndMinutes = workingSettings?.enableMiddayBreak
    ? parseTimeToMinutes(workingSettings.breakEndTime || DEFAULT_BREAK_END_TIME)
    : null;

  const hasActiveBreak =
    breakStartMinutes !== null &&
    breakEndMinutes !== null &&
    breakEndMinutes > breakStartMinutes;

  const dayStart = startOfDay(currentDate);
  dayStart.setHours(0, 0, 0, 0);
  dayStart.setMinutes(workdayStartMinutes);

  const dayEnd = startOfDay(currentDate);
  dayEnd.setHours(0, 0, 0, 0);
  dayEnd.setMinutes(workdayEndMinutes);

  const minutePixelRatio = slotHeight / slotDuration;
  const endHourLabel = format(dayEnd, 'HH:mm');

  // Générer les créneaux horaires
  const generateTimeSlots = () => {
    const slots: Array<{
      id: string;
      startTime: Date;
      endTime: Date;
      duration: number;
      top: number;
    }> = [];

    for (
      let startMinutes = workdayStartMinutes;
      startMinutes + slotDuration <= workdayEndMinutes;
      startMinutes += slotDuration
    ) {
      const slotStart = startOfDay(currentDate);
      slotStart.setHours(0, 0, 0, 0);
      slotStart.setMinutes(startMinutes);

      const slotEnd = addMinutes(slotStart, slotDuration);

      slots.push({
        id: slotStart.toISOString(),
        startTime: slotStart,
        endTime: slotEnd,
        duration: slotDuration,
        top: Math.round((startMinutes - workdayStartMinutes) * minutePixelRatio)
      });
    }

    return slots;
  };

  const timeSlots = generateTimeSlots();
  const gridHeight = timeSlots.length * slotHeight;

  const dayAppointments = appointments.filter((appointment) => {
    const appointmentStart = new Date(appointment.startTime);
    const appointmentEnd = new Date(appointment.endTime);
    return appointmentEnd > dayStart && appointmentStart < dayEnd;
  });

  // Vérifier si un créneau est occupé
  const isSlotOccupied = (slotStart: Date, slotEnd: Date) => {
    return dayAppointments.some(appointment => {
      const appointmentStart = new Date(appointment.startTime);
      const appointmentEnd = new Date(appointment.endTime);
      return appointmentStart < slotEnd && appointmentEnd > slotStart;
    });
  };

  const isSlotDuringBreak = (slotStart: Date, slotEnd: Date) => {
    if (!hasActiveBreak || breakStartMinutes === null || breakEndMinutes === null) {
      return false;
    }

    const startMinutes = slotStart.getHours() * 60 + slotStart.getMinutes();
    const endMinutes = slotEnd.getHours() * 60 + slotEnd.getMinutes();

    return hasMinutesOverlap(startMinutes, endMinutes, breakStartMinutes, breakEndMinutes);
  };

  const getSlotSegments = (slotStart: Date) => {
    const segmentCount = slotDuration / bookingStepMinutes;

    return Array.from({ length: segmentCount }, (_, index) => {
      const segmentStart = addMinutes(slotStart, index * bookingStepMinutes);
      const segmentEnd = addMinutes(segmentStart, bookingStepMinutes);

      return {
        start: segmentStart,
        end: segmentEnd,
      };
    });
  };

  const positionedAppointments = buildPositionedAppointments(
    dayAppointments,
    dayStart,
    dayEnd,
    minutePixelRatio
  );

  const handleSlotKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, slotStart: Date) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      const availableSegment = getSlotSegments(slotStart).find(
        (segment) => !isSlotOccupied(segment.start, segment.end)
      );

      if (availableSegment) {
        onTimeSlotClick(availableSegment.start, bookingStepMinutes);
      }
    }
  };

  const handleHourSlotClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    slotStart: Date,
    slotHeightPx: number
  ) => {
    const segments = getSlotSegments(slotStart);
    const clickY = event.nativeEvent.offsetY;
    const segmentHeight = slotHeightPx / segments.length;
    const segmentIndex = Math.min(
      segments.length - 1,
      Math.max(0, Math.floor(clickY / segmentHeight))
    );

    const clickedSegment = segments[segmentIndex];
    if (clickedSegment && !isSlotOccupied(clickedSegment.start, clickedSegment.end)) {
      onTimeSlotClick(clickedSegment.start, bookingStepMinutes);
      return;
    }

    const fallbackSegment = segments.find((segment) => !isSlotOccupied(segment.start, segment.end));
    if (fallbackSegment) {
      onTimeSlotClick(fallbackSegment.start, bookingStepMinutes);
    }
  };

  const now = new Date();
  const isCurrentDay = isSameDay(currentDate, now);
  const nowTop = Math.round(
    ((now.getTime() - dayStart.getTime()) / 60000) * minutePixelRatio
  );
  const isNowWithinVisibleHours = nowTop >= 0 && nowTop <= gridHeight;

  return (
    <div className="relative h-full overflow-y-auto overflow-x-hidden">
      {/* En-tête avec les heures */}
      <div className="grid grid-cols-12 min-h-full">
        {/* Colonne des heures */}
        <div className="col-span-2 border-r border-gray-200 bg-gray-50">
          <div className="sticky top-0 z-40 h-12 bg-gray-50 px-2 border-b border-gray-200 flex items-center">
            <div className="text-sm font-medium text-gray-700">Heure</div>
          </div>
          {timeSlots.map((slot) => (
            <div
              key={slot.id}
              className="border-b border-gray-100 flex items-start p-2"
              style={{ height: `${slotHeight}px` }}
            >
              <span className="text-sm text-gray-600">
                {format(slot.startTime, 'HH:mm')}
              </span>
            </div>
          ))}
        </div>

        {/* Colonne principale avec les créneaux */}
        <div className="col-span-10 relative">
          <div className="sticky top-0 z-40 h-12 bg-white px-3 border-b border-gray-200 flex items-center">
            <div className="text-base font-medium text-gray-900">
              {format(currentDate, 'EEEE d MMMM', { locale: fr })}
            </div>
          </div>

          {/* Grille des créneaux */}
          <div className="relative">
            {timeSlots.map((slot) => {
              const segments = getSlotSegments(slot.startTime);
              const isEntireHourOccupied = segments.every((segment) => isSlotOccupied(segment.start, segment.end));
              const isEntireSlotBreak = segments.every((segment) => isSlotDuringBreak(segment.start, segment.end));
              const isDisabled = isEntireHourOccupied || isEntireSlotBreak;
              let slotStateClassName = 'hover:bg-blue-50';

              if (isDisabled) {
                slotStateClassName = isEntireSlotBreak
                  ? 'bg-amber-50 cursor-not-allowed'
                  : 'bg-gray-50 cursor-not-allowed';
              }

              return (
                <button
                  key={slot.id}
                  type="button"
                  className={`block w-full border-b border-gray-100 cursor-pointer transition-colors ${slotStateClassName}`}
                  style={{ height: `${slotHeight}px` }}
                  onClick={(event) => handleHourSlotClick(event, slot.startTime, slotHeight)}
                  onKeyDown={(event) => handleSlotKeyDown(event, slot.startTime)}
                  aria-label={`Créer un rendez-vous entre ${format(slot.startTime, 'HH:mm')} et ${format(slot.endTime, 'HH:mm')}`}
                  disabled={isDisabled}
                >
                  {isEntireSlotBreak && (
                    <span className="sr-only">Pause</span>
                  )}
                </button>
              );
            })}

            {/* Ligne de l'heure actuelle */}
            {isCurrentDay && isNowWithinVisibleHours && (
              <div
                className="absolute left-0 right-0 border-t-2 border-red-500 z-30 pointer-events-none"
                style={{
                  top: `${nowTop}px`
                }}
              >
                <div className="bg-red-500 text-white text-xs px-2 py-1 rounded-r inline-block">
                  Maintenant
                </div>
              </div>
            )}

            {positionedAppointments.map((positionedAppointment) => {
              const { appointment, startTime, endTime, top, height, column, columns } = positionedAppointment;
              const columnWidth = 100 / columns;
              const leftGap = 8;
              const widthGap = 10;
              const appointmentDurationMinutes = Math.max(
                1,
                Math.round((endTime.getTime() - startTime.getTime()) / 60000)
              );
              const isShortAppointment = appointmentDurationMinutes <= bookingStepMinutes;
              const isOneHourOrLess = appointmentDurationMinutes <= slotDuration;
              const appointmentTitle = appointment.title || `${appointment.client?.firstName} ${appointment.client?.lastName}`;
              const appointmentTimeRange = `${format(startTime, 'HH:mm')} - ${format(endTime, 'HH:mm')}`;
              const appointmentLongRange = `De ${format(startTime, 'HH:mm')} à ${format(endTime, 'HH:mm')}`;
              const appointmentStartLabel = format(startTime, 'HH:mm');
              let appointmentPaddingClass = APPOINTMENT_CARD_PADDING_REGULAR_CLASS;
              if (isShortAppointment) {
                appointmentPaddingClass = APPOINTMENT_CARD_PADDING_COMPACT_CLASS;
              }

              let appointmentContent: React.ReactNode;
              if (isShortAppointment) {
                appointmentContent = (
                  <div className="truncate leading-tight whitespace-nowrap text-[11px] font-medium">
                    <span>{appointmentTitle}</span>
                    <span className="text-gray-600"> · {appointmentStartLabel}</span>
                  </div>
                );
              } else if (isOneHourOrLess) {
                appointmentContent = (
                  <>
                    <div className={`${APPOINTMENT_CARD_TITLE_CLASS} pr-5`}>
                      {appointmentTitle}
                    </div>
                    <div className={APPOINTMENT_CARD_TIME_CLASS}>
                      {appointmentLongRange}
                    </div>
                  </>
                );
              } else {
                appointmentContent = (
                  <>
                    <div className={`${APPOINTMENT_CARD_TITLE_CLASS} pr-5`}>
                      {appointmentTitle}
                    </div>
                    <div className={APPOINTMENT_CARD_TIME_CLASS}>
                      {appointmentTimeRange}
                    </div>
                    {appointment.client?.company && (
                      <div className={APPOINTMENT_CARD_META_CLASS}>
                        {appointment.client.company}
                      </div>
                    )}
                  </>
                );
              }

              return (
                <button
                  key={appointment.id}
                  type="button"
                  className={`absolute ${APPOINTMENT_CARD_BASE_CLASS} z-20 ${appointmentPaddingClass} ${getAppointmentStatusCardClass(appointment.status)}`}
                  style={{
                    height: `${height}px`,
                    top: `${top}px`,
                    left: `calc(${column * columnWidth}% + ${leftGap}px)`,
                    width: `calc(${columnWidth}% - ${widthGap}px)`
                  }}
                  onClick={() => onAppointmentClick(appointment)}
                  aria-label={`Voir le rendez-vous ${appointment.title || 'sans titre'} de ${format(startTime, 'HH:mm')} à ${format(endTime, 'HH:mm')}`}
                >
                  {appointmentContent}
                  {columns > 1 && (
                    <span className="absolute right-1 top-1 rounded bg-white/70 px-1 text-[10px] font-semibold text-gray-700">
                      {column + 1}/{columns}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-40 grid grid-cols-12 border-t border-slate-300 bg-white/95 backdrop-blur pointer-events-none">
        <div className="col-span-2 border-r border-slate-200 px-2 py-1 text-xs font-semibold text-slate-700">
          {endHourLabel}
        </div>
        <div className="col-span-10 px-3 py-1 text-[11px] text-slate-500">
          Fin de journee
        </div>
      </div>

      {/* Message si aucun rendez-vous */}
      {appointments.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center text-gray-500">
            <div className="text-lg font-medium">Aucun rendez-vous</div>
            <div className="text-sm">Cliquez sur un créneau pour en créer un</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DayView;